// react
import { ReactNode } from 'react';

// types
import { Conversion } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Grid, Tooltip, Typography, TypographyProps } from '@mui/material';

// ui
import { IconCircle, IconCircleProps } from '../IconCircle/IconCircle';

// utils
import { formatCurrency } from '../../../utils/currency';

// props
export interface AmountProps {
	conversionField?: 'amount' | 'tax' | 'total';
	conversions?: Conversion[];
	currency?: null | string;
	hasAsterisk?: boolean;
	IconCircleProps?: IconCircleProps;
	notation?: Intl.NumberFormatOptions['notation'];
	sign?: 'negative' | 'positive';
	TypographyProps?: TypographyProps;
	value: null | number | undefined;
}

export const Amount = ({
	conversionField = 'total',
	conversions = [],
	currency,
	hasAsterisk,
	IconCircleProps,
	notation = 'standard',
	sign = 'positive',
	TypographyProps,
	value,
}: AmountProps) => {
	// hooks
	const { t } = useTranslation();

	let asterisk: ReactNode = '';

	if (hasAsterisk) {
		asterisk = (
			<Typography component="sup" color="error">
				*
			</Typography>
		);
	}

	let content = (
		<Typography>
			{t('na')}
			{asterisk}
		</Typography>
	);

	let conversionItems: ReactNode = <></>;

	if (conversions.length) {
		conversionItems = conversions.map((conversion) => {
			let conversionValue: number;
			if (conversionField === 'amount') {
				conversionValue = conversion.amount;
			} else if (conversionField === 'tax') {
				conversionValue = conversion.tax;
			} else {
				conversionValue = conversion.amount + (conversion.tax || 0);
			}

			if (sign === 'negative') {
				conversionValue = -conversionValue;
			}

			return (
				<Grid item key={conversion.currency}>
					<Typography color="brand.grey.500">
						{formatCurrency({
							currency: conversion.currency,
							value: conversionValue,
						})}
					</Typography>
				</Grid>
			);
		});
	}

	if (typeof value === 'number') {
		const signedValue = sign === 'negative' ? -value : value;
		const amount = formatCurrency({ currency, notation, value: signedValue });
		const amountCompact = formatCurrency({ currency, notation: 'compact', value: signedValue });
		const amountStandard = formatCurrency({ currency, notation: 'standard', value: signedValue });

		const typographyProps = {
			...TypographyProps,
			color: sign === 'negative' ? 'error.main' : TypographyProps?.color,
		};

		content = (
			<Typography component="span" fontWeight={600} {...typographyProps}>
				{amount}
				{asterisk}
			</Typography>
		);

		if (notation === 'compact') {
			content = (
				<Tooltip arrow={true} placement="top" title={amountStandard}>
					<Typography component="span" fontWeight={600} {...typographyProps}>
						{amountCompact}
						{asterisk}
					</Typography>
				</Tooltip>
			);
		}
	}

	return (
		<Grid container spacing={1} sx={{ display: 'inline-flex', width: 'auto' }}>
			{IconCircleProps && (
				<Grid item>
					<IconCircle size="xs" {...IconCircleProps} />
				</Grid>
			)}
			<Grid item>
				<Grid container direction="column">
					<Grid item>{content}</Grid>
					{conversionItems}
				</Grid>
			</Grid>
		</Grid>
	);
};
